import { GroupState, waitForGroupData } from '@/composition/groups'
import config from '@/config'
import type { IPollVotersResponse } from '@/models/Poll'
import Posts, { GoLiveStatus, IPosts } from '@/models/Posts'
import { useGlobalStore } from '@/store/app'
import BaseService from './BaseService'
import { requests } from './HTTPService'
import { ServiceWithStoreDependencies } from './decorators/serviceDecorators'

const http = requests(config?.COMMUNITIES_SERVER_URL)

@ServiceWithStoreDependencies('groupId')
class PostsServiceCls extends BaseService<Posts> {
  constructor() {
    super(null as any)
  }

  model() {
    return Posts
  }

  private async getEndpointPath(): Promise<string> {
    const appStore = useGlobalStore()
    let locationId = appStore.locationId

    if (appStore.isGokollab) {
      /*
        Wait for the group locationId to be set.
        This is needed for gokollab, because the group can have a different locationId.
        and when we switch groups, the locationId is not set immediately.
      */
      await waitForGroupData()
      locationId = GroupState.state.groupData?.locationId
    }

    http.defaults.headers['x-location-id'] = locationId
    return `/${locationId}/groups/${appStore.groupId}`
  }

  get endpoint(): string {
    const appStore = useGlobalStore()
    return `/${appStore.locationId}/groups/${appStore.groupId}`
  }

  async create(data: { channelId: string; [key: string]: any }): Promise<any> {
    const endpointPath = await this.getEndpointPath()
    const response = await http.post(`${endpointPath}/channels/${data.channelId}/posts`, data)
    return response.data
  }

  async getPublicPosts(query?: { [key: string]: any }): Promise<any> {
    const endpointPath = await this.getEndpointPath()
    // set 'x-location-id' to headers

    // TODO: JA - add SWR for mobile app
    // const appStore = useGlobalStore()
    // const endpointUrl = isPwa()
    //   ? `${endpointPath}/public/posts/${appStore.userId}` // added SWR for mobile app
    //   : `${endpointPath}/public/posts`
    const endpointUrl = `${endpointPath}/public/posts`
    const response = await http.get(endpointUrl, {
      params: query,
    })
    return response.data
  }

  async getPost(postId: string): Promise<any> {
    const endpointPath = await this.getEndpointPath()
    const response = await http.get(`${endpointPath}/posts/${postId}`)
    return response.data
  }

  async getChannelPosts(channelId: string, query?: { [key: string]: any }): Promise<any> {
    const endpointPath = await this.getEndpointPath()
    const response = await http.get(`${endpointPath}/channels/${channelId}/posts`, { params: query })
    return response.data
  }

  async pinPostToChannel(postId: string, channelId: string): Promise<any> {
    const endpointPath = await this.getEndpointPath()
    const response = await http.patch(`${endpointPath}/channels/${channelId}/posts/${postId}`, { action: 'PIN_TO_CHANNEL' })
    return response.data
  }

  async pinPostToHome(postId: string, channelId: string): Promise<any> {
    const endpointPath = await this.getEndpointPath()
    const response = await http.patch(`${endpointPath}/channels/${channelId}/posts/${postId}`, { action: 'PIN_TO_HOME' })
    return response.data
  }

  async unpinPostFromChannel(postId: string, channelId: string): Promise<any> {
    const endpointPath = await this.getEndpointPath()
    const response = await http.patch(`${endpointPath}/channels/${channelId}/posts/${postId}`, { action: 'UNPIN_FROM_CHANNEL' })
    return response.data
  }

  async unpinPostFromHome(postId: string, channelId: string): Promise<any> {
    const endpointPath = await this.getEndpointPath()
    const response = await http.patch(`${endpointPath}/channels/${channelId}/posts/${postId}`, { action: 'UNPIN_FROM_HOME' })
    return response.data
  }

  async getChannelPinnedPosts(channelId: string, paginationParams: { page?: number; limit?: number }): Promise<any> {
    const endpointPath = await this.getEndpointPath()
    const response = await http.get(`${endpointPath}/channels/${channelId}/posts/pinned`, { params: paginationParams })
    return response.data
  }

  async getHomePinnedPosts(paginationParams: { page?: number; limit?: number }): Promise<any> {
    const endpointPath = await this.getEndpointPath()
    const response = await http.get(`${endpointPath}/posts/pinned`, {
      params: paginationParams,
    })
    return response.data
  }

  async updatePostChannel(postId: string, channelId: string): Promise<any> {
    const endpointPath = await this.getEndpointPath()
    const response = await http.post(`${endpointPath}/posts/${postId}/update-channel`, { channelId })
    return response.data
  }

  async getUserHomeTimeline(userId: string): Promise<any> {
    const endpointPath = await this.getEndpointPath()
    const response = await http.get(`${endpointPath}/${userId}/home-timeline`)
    return response.data
  }

  async updatePostStatus(postId: string, licenseId: string, status: GoLiveStatus): Promise<any> {
    const endpointPath = await this.getEndpointPath()
    const response = await http.patch(`${endpointPath}/posts/${postId}/live-status`, {
      licenseId: licenseId,
      status: status,
    })
    return response.data
  }

  async updatePost(postData: IPosts, channelId: string, postId: string): Promise<any> {
    const endpointPath = await this.getEndpointPath()
    const response = await http.patch(`${endpointPath}/channels/${channelId}/posts/${postId}`, {
      action: 'UPDATE_POST',
      post: postData,
    })
    return response.data
  }

  async getPostsByUser(params: { [key: string]: any }): Promise<any> {
    const endpointPath = await this.getEndpointPath()
    const response = await http.get(`${endpointPath}/posts`, { params })
    return response.data
  }

  async deletePost(postId: string): Promise<any> {
    const endpointPath = await this.getEndpointPath()
    const response = await http.delete(`${endpointPath}/posts/${[postId]}`)
    return response.data
  }

  async toggleCommentsDisabled(postId: string, channelId: string, currentlyDisabled: boolean): Promise<any> {
    const endpointPath = await this.getEndpointPath()
    const action = currentlyDisabled ? 'ENABLE_COMMENTS' : 'DISABLE_COMMENTS'

    const response = await http.patch(`${endpointPath}/channels/${channelId}/posts/${postId}`, { action })

    return response.data
  }

  async markPostsAsReadBulk(
    posts: { postId: string; channelId: string }[]
  ): Promise<{ channelId: string; postId: string; wasAlreadyRead: boolean }[]> {
    const response = await http.post(`${this.endpoint}/posts/read-bulk`, {
      posts,
    })
    return response.data
  }

  async votePoll(pollId: string, optionId: string): Promise<{ success: true }> {
    const endpointPath = await this.getEndpointPath()
    const response = await http.post(`${endpointPath}/polls/${pollId}/vote`, {
      optionId,
    })
    return response.data
  }

  async deselectPoll(pollId: string, optionId: string): Promise<{ success: true }> {
    const endpointPath = await this.getEndpointPath()
    const response = await http.delete(`${endpointPath}/polls/${pollId}/vote`, {
      data: { optionId },
    })
    return response.data
  }

  async getPollVoters(pollId: string, optionId: string, query?: { limit?: number; previousId?: string }): Promise<IPollVotersResponse> {
    const endpointPath = await this.getEndpointPath()
    const response = await http.get(`${endpointPath}/polls/${pollId}/options/${optionId}/voters`, {
      params: query,
    })
    return response.data
  }
}

// Export singleton instance
export const PostsService = new PostsServiceCls()

// Export type for dependency injection
export type IPostsService = PostsServiceCls

// Export commonly used methods directly
export const {
  create,
  getPublicPosts,
  getPost,
  getChannelPosts,
  pinPostToChannel,
  pinPostToHome,
  unpinPostFromChannel,
  unpinPostFromHome,
  getChannelPinnedPosts,
  getHomePinnedPosts,
  updatePostChannel,
  getUserHomeTimeline,
  updatePost,
  updatePostStatus,
  getPostsByUser,
  deletePost,
  toggleCommentsDisabled,
  votePoll,
  deselectPoll,
  getPollVoters,
} = PostsService
